// CRM Attribution — operator screen (cross-client). Maps sending accounts and
// campaigns to clients and offerings so every graded reply is credited to the
// right client and offering. Reads /market/attribution (overview) and
// /market/attribution/campaigns (paged list). Super-admin / team-member only.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';

const PAGE = 25;
const TABS = [
  { key: 'all', label: 'All' },
  { key: 'needs_client', label: 'Needs client' },
  { key: 'needs_offering', label: 'Needs offering' },
  { key: 'quiet', label: 'Quiet 7d' }
];
const SORTS = [
  { key: 'last_activity', label: 'Last activity' },
  { key: 'reply_rate', label: 'Reply rate' },
  { key: 'replies', label: 'Replies' },
  { key: 'name', label: 'Name' }
];

function relTime(ts) {
  if (!ts) return 'never';
  const s = Math.max(0, (Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return Math.round(s / 60) + ' min ago';
  if (s < 86400) return Math.round(s / 3600) + 'h ago';
  if (s < 86400 * 30) return Math.round(s / 86400) + 'd ago';
  return new Date(ts).toLocaleDateString('en-GB');
}
function pct(n, d) { return d > 0 ? Math.round((100 * n) / d) : null; }
function initials(s) {
  return (s || '?').replace(/[^A-Za-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
}
function campaignStatus(c) {
  if (!c.client_id) return { key: 'client', label: 'Needs client' };
  if (!c.offering_id) return { key: 'offering', label: 'Needs offering' };
  return { key: 'ok', label: 'Resolved' };
}
function sellerHealth(s) {
  if (!s.last_seen_at) return { key: 'never', label: 'No payloads yet' };
  const hrs = (Date.now() - new Date(s.last_seen_at).getTime()) / 3600000;
  if (hrs > 24 && s.campaign_count > 0) return { key: 'quiet', label: 'Quiet ' + relTime(s.last_seen_at).replace(' ago', '') };
  return { key: 'live', label: 'Active ' + relTime(s.last_seen_at) };
}
function offeringSource(c) {
  if (c.offering_source === 'manual') return 'set manually';
  if (c.offering_source === 'extracted') {
    const n = c.offering_steps_seen || 0;
    return 'extracted from ' + n + (n === 1 ? ' step' : ' steps');
  }
  return 'not detected yet';
}

const EMPTY_SELLER = { external_id: '', email: '', label: '', mode: 'shared', client_id: '' };

export default function CrmAttribution() {
  const { getToken } = useAuth();

  const api = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const r = await fetch(`${API_BASE}${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) }
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok || d.ok === false) throw new Error(d.error || `Request failed (${r.status})`);
    return d;
  }, [getToken]);

  const [ov, setOv] = useState(null);
  const [clients, setClients] = useState([]);
  const [list, setList] = useState({ rows: [], total: 0, counts: {} });
  const [loadingList, setLoadingList] = useState(true);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [tab, setTab] = useState('all');
  const [sellerF, setSellerF] = useState('');
  const [clientF, setClientF] = useState('');
  const [sort, setSort] = useState('last_activity');
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [savingId, setSavingId] = useState(null);
  const [sellerModal, setSellerModal] = useState(null);
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkValue, setBulkValue] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadOverview = useCallback(async () => {
    try { setOv(await api('/market/attribution')); } catch (e) { toast.error(e.message); }
  }, [api]);

  const loadClients = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/clients`);
      if (r.ok) {
        const d = await r.json();
        setClients((Array.isArray(d) ? d : []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => { setOffset(0); setSelected(new Set()); }, [debounced, tab, sellerF, clientF, sort]);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    const p = new URLSearchParams({ tab, sort, limit: String(PAGE), offset: String(offset) });
    if (debounced) p.set('q', debounced);
    if (sellerF) p.set('seller_id', sellerF);
    if (clientF) p.set('client_id', clientF);
    try {
      const d = await api('/market/attribution/campaigns?' + p.toString());
      setList({ rows: d.rows || [], total: d.total || 0, counts: d.counts || {} });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingList(false);
    }
  }, [api, tab, sort, offset, debounced, sellerF, clientF]);

  useEffect(() => { loadOverview(); loadClients(); }, [loadOverview, loadClients]);
  useEffect(() => { loadList(); }, [loadList]);

  const refresh = useCallback(() => { loadOverview(); loadList(); }, [loadOverview, loadList]);

  const clientName = useCallback(id => {
    const c = clients.find(x => String(x.id) === String(id));
    return c ? c.name : (id ? 'client ' + id : 'none');
  }, [clients]);
  const offeringName = useCallback(id => {
    const o = ((ov && ov.offerings) || []).find(x => String(x.id) === String(id));
    return o ? o.label : (id ? 'offering ' + id : 'none');
  }, [ov]);
  const campaignName = useCallback(id => {
    const c = list.rows.find(x => x.id === id);
    return c ? c.campaign_name || ('campaign #' + id) : 'campaign #' + id;
  }, [list.rows]);

  const status = useMemo(() => {
    const m = { resolved: 0, partial: 0, pending: 0 };
    ((ov && ov.status) || []).forEach(x => { m[x.status] = x.n; });
    return m;
  }, [ov]);
  const cov = ov && ov.segment_coverage;
  const covPct = cov && cov.total ? pct(cov.with_segment, cov.total) : null;
  const parkedN = ((ov && ov.parked) || []).reduce((n, p) => n + p.n, 0);

  async function patchCampaign(id, body, okMsg) {
    setSavingId(id);
    try {
      const d = await api(`/market/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
      const n = d.signals_moved || 0;
      toast.success(okMsg + (n ? ` · ${n} ${n === 1 ? 'signal' : 'signals'} updated` : ''));
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingId(null);
    }
  }

  async function runBulk() {
    const ids = [...selected];
    if (!ids.length) return;
    const body = { ids };
    const v = bulkValue === '' ? null : Number(bulkValue);
    if (bulkModal === 'client') body.client_id = v; else body.offering_id = v;
    setBusy(true);
    try {
      const d = await api('/market/campaigns/bulk', { method: 'POST', body: JSON.stringify(body) });
      const results = d.results || [];
      const failed = results.filter(r => !r.ok);
      const moved = results.reduce((n, r) => n + (r.signals_moved || 0), 0);
      toast.success(`${d.updated} ${d.updated === 1 ? 'campaign' : 'campaigns'} updated · ${moved} ${moved === 1 ? 'signal' : 'signals'}`);
      if (failed.length) toast.error(`${failed.length} could not be updated`);
      setBulkModal(null); setBulkValue(''); setSelected(new Set());
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveSeller() {
    const f = sellerModal.form;
    if (!f.external_id.trim()) { toast.error('Enter the LinkedIn account id'); return; }
    if (f.mode === 'dedicated' && !f.client_id) { toast.error('Choose the client this account sells for'); return; }
    setBusy(true);
    try {
      await api('/market/sellers', {
        method: 'POST',
        body: JSON.stringify({
          external_id: f.external_id.trim(),
          email: f.email.trim() || null,
          label: f.label.trim() || null,
          client_id: f.mode === 'dedicated' ? Number(f.client_id) : null
        })
      });
      toast.success(sellerModal.mode === 'register' ? 'Account registered' : 'Account updated');
      setSellerModal(null);
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function doReattribute() {
    setBusy(true);
    try {
      await api('/market/attribution/reattribute', { method: 'POST' });
      toast.success('Attribution refreshed');
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false); setConfirm(null);
    }
  }

  async function doReplay() {
    setBusy(true);
    try {
      const since = new Date(Date.now() - 7 * 86400000).toISOString();
      const d = await api('/market/inbound/replay', { method: 'POST', body: JSON.stringify({ since, limit: 500 }) });
      const results = d.results || [];
      const dup = results.filter(r => r.result && r.result.duplicate).length;
      toast.success(`${d.replayed} ${d.replayed === 1 ? 'payload' : 'payloads'} replayed · ${dup} already counted`);
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false); setConfirm(null);
    }
  }

  function toggle(id) {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }
  const allOnPage = list.rows.length > 0 && list.rows.every(r => selected.has(r.id));
  function togglePage() {
    setSelected(s => {
      const n = new Set(s);
      if (allOnPage) list.rows.forEach(r => n.delete(r.id)); else list.rows.forEach(r => n.add(r.id));
      return n;
    });
  }

  function eventText(e) {
    const who = e.actor === 'system:extract' ? 'Sonar' : e.actor === 'system:re-extract' ? 'Sonar (richer content)' : e.actor;
    if (e.entity === 'campaign' && e.field === 'client_id') {
      return e.new_value
        ? `${who} set the client on ${campaignName(e.entity_id)} to ${clientName(e.new_value)}`
        : `${who} cleared the client on ${campaignName(e.entity_id)}`;
    }
    if (e.entity === 'campaign' && e.field === 'offering_id') {
      if (e.actor === 'system:extract') return `Sonar detected ${offeringName(e.new_value)} on ${campaignName(e.entity_id)}`;
      return e.new_value
        ? `${who} set the offering on ${campaignName(e.entity_id)} to ${offeringName(e.new_value)}`
        : `${who} reset the offering on ${campaignName(e.entity_id)} to automatic`;
    }
    if (e.entity === 'seller' && e.field === 'registered') return `${who} registered sending account ${e.new_value}`;
    if (e.entity === 'seller' && e.field === 'client_id') {
      return e.new_value
        ? `${who} set a default client of ${clientName(e.new_value)} on a sending account`
        : `${who} made a sending account shared`;
    }
    if (e.entity === 'inbound_log' && e.field === 'replay') return `${who} replayed ${e.new_value}`;
    return `${who} changed ${e.field} on ${e.entity}`;
  }

  const sellers = (ov && ov.sellers) || [];
  const unregistered = (ov && ov.unregistered_sellers) || [];
  const offerings = (ov && ov.offerings) || [];
  const events = (ov && ov.events) || [];
  const inbound = (ov && ov.inbound) || {};
  const counts = list.counts || {};

  return (
    <div className="crmcl crmat">
      <div className="crmcl-head">
        <div>
          <h1 className="crmcl-title">Attribution</h1>
          <div className="crmcl-sub">
            Who sent it, and what was sold. Last payload {relTime(inbound.last_received_at)} · {inbound.last_24h || 0} in 24h
            {inbound.last_test_at && <> · LinkedHelper test received {relTime(inbound.last_test_at)}</>}
          </div>
        </div>
        <div className="crmat-head-actions">
          <button className="crmcl-act crmat-headbtn" disabled={busy} onClick={() => setConfirm({
            title: 'Replay the last 7 days',
            body: 'Every payload received in the last 7 days runs through the receiver again. Replies already counted are recognised and skipped, so nothing is double counted.',
            label: 'Replay', action: doReplay
          })}>Replay</button>
          <button className="crmcl-act crmat-headbtn" disabled={busy} onClick={() => setConfirm({
            title: 'Refresh attribution',
            body: 'Links campaigns to registered accounts, detects missing offerings, and credits any waiting signals. This also runs on its own every 30 minutes.',
            label: 'Refresh', action: doReattribute
          })}>Reattribute</button>
        </div>
      </div>

      <div className="crmat-tiles">
        <div className="crmat-tile"><div className="crmat-tile-l">Resolved</div><div className="crmat-tile-v crmat-v--ok">{status.resolved}</div></div>
        <div className="crmat-tile"><div className="crmat-tile-l">Needs mapping</div><div className="crmat-tile-v crmat-v--warn">{status.partial}</div></div>
        <div className="crmat-tile"><div className="crmat-tile-l">Unattributed</div><div className="crmat-tile-v crmat-v--muted">{status.pending}</div></div>
        <div className="crmat-tile">
          <div className="crmat-tile-l">Segment coverage</div>
          <div className={'crmat-tile-v ' + (covPct === null ? 'crmat-v--muted' : covPct < 50 ? 'crmat-v--bad' : 'crmat-v--ok')}>{covPct === null ? '–' : covPct + '%'}</div>
        </div>
      </div>

      {((covPct !== null && covPct < 50) || parkedN > 0) && (
        <div className="crmat-alert">
          <span className="crmat-alert-ic">!</span>
          <span>
            {covPct !== null && covPct < 50 && <>Only {cov.with_segment} of {cov.total} signals carry company firmographics, so segment matches can't be trusted yet. </>}
            {parkedN > 0 && <>{parkedN} other {parkedN === 1 ? 'payload is' : 'payloads are'} parked ({(ov.parked || []).map(p => p.source).join(', ')}).</>}
          </span>
        </div>
      )}

      <div className="crmat-sechead">
        <span>Sending accounts</span>
        <button className="crmcl-act" onClick={() => setSellerModal({ mode: 'register', form: { ...EMPTY_SELLER } })}>Add account</button>
      </div>
      <div className="crmat-accounts">
        {sellers.length === 0 && unregistered.length === 0 && (
          <div className="crmat-empty">No sending accounts yet. They appear here as soon as LinkedHelper sends its first payload.</div>
        )}
        {sellers.map(s => {
          const h = sellerHealth(s);
          return (
            <div key={s.id} className="crmat-account">
              <div className="crmat-avatar">{initials(s.label || s.external_id)}</div>
              <div className="crmat-account-main">
                <div className="crmat-account-name">{s.label || s.external_id}</div>
                <div className="crmat-account-meta">LinkedIn · {s.external_id}{s.email ? ' · ' + s.email : ''} · {s.campaign_count} {s.campaign_count === 1 ? 'campaign' : 'campaigns'}</div>
              </div>
              <div className="crmat-account-health">
                <div><span className={'crmat-dot crmat-dot--' + h.key} />{h.label}</div>
                <div>{s.payloads_7d || 0} payloads, 7 days</div>
              </div>
              {s.client_id
                ? <span className="crmcl-chip crmcl-chip--acct">{s.client_name || clientName(s.client_id)}</span>
                : <span className="crmat-chip-shared">Shared</span>}
              <button className="crmcl-act" onClick={() => setSellerModal({
                mode: 'edit',
                form: { external_id: s.external_id, email: s.email || '', label: s.label || '', mode: s.client_id ? 'dedicated' : 'shared', client_id: s.client_id ? String(s.client_id) : '' }
              })}>Edit</button>
            </div>
          );
        })}
        {unregistered.map(u => (
          <div key={u.seller_key} className="crmat-account crmat-account--new">
            <div className="crmat-avatar crmat-avatar--new">?</div>
            <div className="crmat-account-main">
              <div className="crmat-account-name">New LinkedIn account seen: {u.my_id || u.seller_key}</div>
              <div className="crmat-account-meta">{u.my_email ? u.my_email + ' · ' : ''}last payload {relTime(u.last_seen_at)}. Replies are captured and wait here until it's registered.</div>
            </div>
            <button className="crmcl-new" onClick={() => setSellerModal({
              mode: 'register',
              form: { ...EMPTY_SELLER, external_id: u.my_id || u.seller_key, email: u.my_email || '' }
            })}>Register</button>
          </div>
        ))}
      </div>

      <div className="crmat-sechead"><span>Campaigns</span></div>
      <div className="crmcl-toolbar">
        <div className="crmcl-search">
          <svg viewBox="0 0 16 16" width="14" height="14"><circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <input placeholder="Search campaigns, clients, offerings or campaign number" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <select className="crmcl-select" value={sellerF} onChange={e => setSellerF(e.target.value)}>
          <option value="">All accounts</option>
          {sellers.map(s => <option key={s.id} value={s.id}>{s.label || s.external_id}</option>)}
        </select>
        <select className="crmcl-select" value={clientF} onChange={e => setClientF(e.target.value)}>
          <option value="">All clients</option>
          <option value="none">No client yet</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="crmcl-select" value={sort} onChange={e => setSort(e.target.value)}>
          {SORTS.map(s => <option key={s.key} value={s.key}>Sort: {s.label}</option>)}
        </select>
      </div>

      <div className="crmat-tabs">
        {TABS.map(t => (
          <button key={t.key} className={'crmat-tab' + (tab === t.key ? ' crmat-tab--on' : '')} onClick={() => setTab(t.key)}>
            {t.label} <span className="crmat-tab-n">{counts[t.key] != null ? counts[t.key] : 0}</span>
          </button>
        ))}
      </div>

      <div className="crmat-list">
        <div className="crmat-row crmat-row--head">
          <span><input type="checkbox" checked={allOnPage} onChange={togglePage} aria-label="Select page" /></span>
          <span>Campaign</span><span>Client</span><span>Offering</span><span>Reach</span><span>Reply</span><span>Status</span>
        </div>

        {loadingList && list.rows.length === 0 && <div className="crmat-empty">Loading campaigns...</div>}
        {!loadingList && list.rows.length === 0 && (
          <div className="crmat-empty">
            {tab === 'all' && !debounced && !sellerF && !clientF
              ? 'Campaigns appear here as soon as LinkedHelper sends its first payload.'
              : 'No campaigns match these filters.'}
          </div>
        )}

        {list.rows.map(c => {
          const stt = campaignStatus(c);
          const open = openId === c.id;
          const campEvents = events.filter(e => e.entity === 'campaign' && e.entity_id === c.id);
          const rungTotal = (c.positive || 0) + (c.neutral || 0) + (c.negative || 0);
          return (
            <div key={c.id} className={'crmat-item' + (open ? ' crmat-item--open' : '') + (selected.has(c.id) ? ' crmat-item--sel' : '')}>
              <div className="crmat-row" onClick={() => setOpenId(open ? null : c.id)}>
                <span onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} aria-label={'Select ' + (c.campaign_name || 'campaign')} />
                </span>
                <span className="crmat-cell-main">
                  <span className="crmat-cname">{c.campaign_name || 'Untitled campaign'}</span>
                  <span className="crmat-cmeta">#{c.external_campaign_id} · {c.seller_label || c.seller_key} · {relTime(c.last_signal_at && c.last_signal_at > c.last_seen_at ? c.last_signal_at : c.last_seen_at)}</span>
                </span>
                <span className={c.client_id ? 'crmat-ell' : 'crmat-ell crmat-need'}>{c.client_id ? (c.client_name || clientName(c.client_id)) : 'Choose client'}</span>
                <span className={c.offering_id ? 'crmat-ell' : 'crmat-ell crmat-need'}>{c.offering_id ? (c.offering_label || offeringName(c.offering_id)) : 'Not detected'}</span>
                <span>{c.contacted} → {c.replied}</span>
                <span>{c.reply_rate != null ? c.reply_rate + '%' : '–'}</span>
                <span><span className={'crmat-pill crmat-pill--' + stt.key}>{stt.label}</span></span>
              </div>

              {open && (
                <div className="crmat-detail">
                  <div className="crmat-funnel">
                    <div><b>{c.contacted}</b><span>Contacted</span></div>
                    <div><b>{c.invited}</b><span>Invited</span></div>
                    <div><b>{c.accepted}</b><span>Accepted{c.accept_rate != null ? ' · ' + c.accept_rate + '%' : ''}</span></div>
                    <div><b>{c.messaged}</b><span>Messaged</span></div>
                    <div><b>{c.replied}</b><span>Replied{c.reply_rate != null ? ' · ' + c.reply_rate + '%' : ''}</span></div>
                  </div>

                  {rungTotal > 0 && (
                    <>
                      <div className="crmat-rungbar">
                        <div className="crmat-rung--pos" style={{ flex: c.positive || 0 }} />
                        <div className="crmat-rung--neu" style={{ flex: c.neutral || 0 }} />
                        <div className="crmat-rung--neg" style={{ flex: c.negative || 0 }} />
                      </div>
                      <div className="crmat-rungtxt">{c.positive} positive · {c.neutral} neutral · {c.negative} negative, from {c.signal_count} graded {c.signal_count === 1 ? 'reply' : 'replies'}</div>
                    </>
                  )}

                  <div className="crmat-pickers">
                    <div className="crmcl-field">
                      <label>Client</label>
                      <select value={c.client_id || ''} disabled={savingId === c.id}
                        onChange={e => patchCampaign(c.id, { client_id: e.target.value === '' ? null : Number(e.target.value) }, 'Client updated')}>
                        <option value="">No client</option>
                        {clients.map(cl => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
                      </select>
                    </div>
                    <div className="crmcl-field">
                      <label>Offering · {offeringSource(c)}</label>
                      <select value={c.offering_source === 'manual' ? String(c.offering_id) : (c.offering_id ? String(c.offering_id) : '')} disabled={savingId === c.id}
                        onChange={e => patchCampaign(c.id, { offering_id: e.target.value === '' ? null : Number(e.target.value) },
                          e.target.value === '' ? 'Offering set to automatic' : 'Offering updated')}>
                        <option value="">Detect automatically</option>
                        {offerings.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                      </select>
                    </div>
                  </div>

                  {c.offering_reasoning && c.offering_source !== 'manual' && (
                    <div className="crmat-why">Why this offering: {c.offering_reasoning}</div>
                  )}

                  {campEvents.length > 0 && (
                    <div className="crmat-camp-events">
                      {campEvents.slice(0, 5).map(e => (
                        <div key={e.id}><span>{relTime(e.at)}</span>{eventText(e)}{e.signals_affected ? ` · ${e.signals_affected} ${e.signals_affected === 1 ? 'signal' : 'signals'}` : ''}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {list.total > 0 && (
          <div className="crmat-pager">
            <span>{offset + 1}–{Math.min(offset + PAGE, list.total)} of {list.total}</span>
            <span>
              <button className="crmcl-act" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Prev</button>
              <button className="crmcl-act" disabled={offset + PAGE >= list.total} onClick={() => setOffset(offset + PAGE)}>Next</button>
            </span>
          </div>
        )}
      </div>

      <div className="crmat-sechead"><span>Change history</span></div>
      <div className="crmat-history">
        {events.length === 0
          ? <div className="crmat-empty">No changes yet. Every client, offering and account change is recorded here.</div>
          : events.map(e => (
            <div key={e.id} className="crmat-hrow">
              <span className="crmat-htime">{relTime(e.at)}</span>
              <span className="crmat-htext">{eventText(e)}</span>
              <span className="crmat-hcount">{e.signals_affected ? `${e.signals_affected} ${e.signals_affected === 1 ? 'signal' : 'signals'}` : ''}</span>
            </div>
          ))}
      </div>

      {selected.size > 0 && (
        <div className="crmat-bulkbar">
          <span className="crmat-bulkbar-n">{selected.size} selected</span>
          <span style={{ flex: 1 }} />
          <button className="crmcl-act" onClick={() => { setBulkValue(''); setBulkModal('offering'); }}>Set offering</button>
          <button className="crmcl-new" onClick={() => { setBulkValue(''); setBulkModal('client'); }}>Assign client</button>
          <button className="crmcl-act" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {bulkModal && (
        <div className="crmcl-overlay" onClick={() => !busy && setBulkModal(null)}>
          <div className="crmcl-modal crmat-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="crmcl-modal-head">
              <h2>{bulkModal === 'client' ? 'Assign client' : 'Set offering'}</h2>
              <button className="crmcl-x" onClick={() => setBulkModal(null)}>×</button>
            </div>
            <p className="crmat-modal-txt">
              Applies to {selected.size} {selected.size === 1 ? 'campaign' : 'campaigns'}. Their graded replies move with them, and each change is recorded.
            </p>
            <div className="crmcl-field">
              <label>{bulkModal === 'client' ? 'Client' : 'Offering'}</label>
              <select value={bulkValue} onChange={e => setBulkValue(e.target.value)}>
                <option value="">{bulkModal === 'client' ? 'No client' : 'Detect automatically'}</option>
                {bulkModal === 'client'
                  ? clients.map(cl => <option key={cl.id} value={cl.id}>{cl.name}</option>)
                  : offerings.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div className="crmcl-modal-actions">
              <button className="crmcl-btn crmcl-btn--ghost" onClick={() => setBulkModal(null)} disabled={busy}>Cancel</button>
              <button className="crmcl-btn crmcl-btn--primary" onClick={runBulk} disabled={busy}>{busy ? 'Applying...' : 'Apply'}</button>
            </div>
          </div>
        </div>
      )}

      {sellerModal && (
        <div className="crmcl-overlay" onClick={() => !busy && setSellerModal(null)}>
          <div className="crmcl-modal crmat-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="crmcl-modal-head">
              <h2>{sellerModal.mode === 'register' ? 'Register sending account' : 'Edit sending account'}</h2>
              <button className="crmcl-x" onClick={() => setSellerModal(null)}>×</button>
            </div>
            <div className="crmcl-grid">
              <div className="crmcl-field crmcl-col2">
                <label>LinkedIn account id (sent by LinkedHelper)</label>
                <input value={sellerModal.form.external_id} disabled={sellerModal.mode === 'edit'}
                  onChange={e => setSellerModal({ ...sellerModal, form: { ...sellerModal.form, external_id: e.target.value } })}
                  placeholder="as shown in the first LinkedHelper payload" />
              </div>
              <div className="crmcl-field">
                <label>LinkedIn login email (optional)</label>
                <input value={sellerModal.form.email}
                  onChange={e => setSellerModal({ ...sellerModal, form: { ...sellerModal.form, email: e.target.value } })}
                  placeholder="email used to sign in to LinkedIn" />
              </div>
              <div className="crmcl-field">
                <label>Display name</label>
                <input value={sellerModal.form.label}
                  onChange={e => setSellerModal({ ...sellerModal, form: { ...sellerModal.form, label: e.target.value } })}
                  placeholder="e.g. Jimmy Braimah" />
              </div>
              <div className="crmcl-field crmcl-col2">
                <label>How this account sells</label>
                <div className="crmat-modes">
                  <label className={'crmat-mode' + (sellerModal.form.mode === 'shared' ? ' crmat-mode--on' : '')}>
                    <input type="radio" checked={sellerModal.form.mode === 'shared'}
                      onChange={() => setSellerModal({ ...sellerModal, form: { ...sellerModal.form, mode: 'shared' } })} />
                    <span><b>Shared</b>Sells for several clients. Each campaign is mapped to its client.</span>
                  </label>
                  <label className={'crmat-mode' + (sellerModal.form.mode === 'dedicated' ? ' crmat-mode--on' : '')}>
                    <input type="radio" checked={sellerModal.form.mode === 'dedicated'}
                      onChange={() => setSellerModal({ ...sellerModal, form: { ...sellerModal.form, mode: 'dedicated' } })} />
                    <span><b>Dedicated</b>Sells for one client only. New campaigns are mapped to it automatically.</span>
                  </label>
                </div>
              </div>
              {sellerModal.form.mode === 'dedicated' && (
                <div className="crmcl-field crmcl-col2">
                  <label>Client</label>
                  <select value={sellerModal.form.client_id}
                    onChange={e => setSellerModal({ ...sellerModal, form: { ...sellerModal.form, client_id: e.target.value } })}>
                    <option value="">Choose client</option>
                    {clients.map(cl => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="crmcl-modal-actions">
              <button className="crmcl-btn crmcl-btn--ghost" onClick={() => setSellerModal(null)} disabled={busy}>Cancel</button>
              <button className="crmcl-btn crmcl-btn--primary" onClick={saveSeller} disabled={busy}>
                {busy ? 'Saving...' : sellerModal.mode === 'register' ? 'Register account' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <div className="crmcl-overlay" onClick={() => !busy && setConfirm(null)}>
          <div className="crmcl-modal crmat-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="crmcl-modal-head">
              <h2>{confirm.title}</h2>
              <button className="crmcl-x" onClick={() => setConfirm(null)}>×</button>
            </div>
            <p className="crmat-modal-txt">{confirm.body}</p>
            <div className="crmcl-modal-actions">
              <button className="crmcl-btn crmcl-btn--ghost" onClick={() => setConfirm(null)} disabled={busy}>Cancel</button>
              <button className="crmcl-btn crmcl-btn--primary" onClick={confirm.action} disabled={busy}>{busy ? 'Working...' : confirm.label}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
